import React, { useState, useRef, useEffect } from 'react';
import '../../styles/AddCustomerModal.css';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../config/apiConfig.js';
import fetchApi from '../../utils/api.js';
import Swal from 'sweetalert2';


const CreatableSelect = ({ value, onChange, options, onAddOption, placeholder, onKeyDown }) => {
    const [isOpen, setIsOpen] = useState(false);

    const [searchTerm, setSearchTerm] = useState(value || '');
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (isOpen && activeIndex >= 0 && dropdownRef.current) {
            const activeEl = dropdownRef.current.children[activeIndex];
            if (activeEl) {
                activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [activeIndex, isOpen]);

    useEffect(() => {
        setSearchTerm(value || '');
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const newVal = e.target.value;
        setSearchTerm(newVal);
        onChange(newVal);
        setIsOpen(true);
        setActiveIndex(0);
    };

    const handleOptionClick = (opt) => {
        onChange(opt);
        setSearchTerm(opt);
        setIsOpen(false);
        setActiveIndex(-1);
    };

    const handleBlur = (e) => {
        // Use a small delay to allow clicking on options in the dropdown
        setTimeout(() => {
            if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
                setIsOpen(false);
            }
        }, 150);
    };

    const handleAddClick = () => {
        onAddOption(searchTerm);
        setIsOpen(false);
        setActiveIndex(-1);
    };

    const filteredOptions = (options || []).filter(opt =>
        opt && opt.toString().toLowerCase().includes((searchTerm || '').toLowerCase())
    );

    const showAddOption = onAddOption && searchTerm && !filteredOptions.some(opt => opt && opt.toString().toLowerCase() === searchTerm.toLowerCase());

    const handleInternalKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'Enter') {
                // If closed and Enter is pressed, do NOT open again if we just selected something.
                // Instead, allow the parent's onKeyDown to handle it (move to next field)
                if (typeof onKeyDown === 'function') {
                    onKeyDown(e);
                }
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
                setActiveIndex(0);
            }
            return;
        }

        const totalOptions = filteredOptions.length + (showAddOption ? 1 : 0);

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => (prev + 1) % totalOptions);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => (prev - 1 + totalOptions) % totalOptions);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
                handleOptionClick(filteredOptions[activeIndex]);
            } else if (showAddOption && activeIndex === filteredOptions.length) {
                handleAddClick();
            } else {
                setIsOpen(false);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <div className="custom-select-container" ref={containerRef} style={{ width: '100%' }}>
            <input
                className="add-customer-input"
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleInternalKeyDown}
                onFocus={() => {
                    setIsOpen(true);
                    setActiveIndex(0);
                }}
                onBlur={handleBlur}
                placeholder={placeholder || "Select or Type..."}
                style={{ width: '100%' }}
            />
            {isOpen && (filteredOptions.length > 0 || showAddOption) && (
                <div className="custom-select-dropdown" ref={dropdownRef}>
                    {filteredOptions.map((opt, index) => (
                        <div
                            key={opt}
                            className="custom-select-option"
                            style={{ backgroundColor: index === activeIndex ? '#f3f4f6' : 'transparent' }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleOptionClick(opt);
                            }}
                        >
                            {opt}
                        </div>
                    ))}
                    {showAddOption && (
                        <div
                            className="custom-select-add-option"
                            style={{ backgroundColor: activeIndex === filteredOptions.length ? '#f3f4f6' : 'transparent' }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleAddClick();
                            }}
                        >
                            Add "{searchTerm}"
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

function AddCustomerModal({ isOpen, onClose, isPage, onSuccess, onKeyDown }) {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const isSuperAdmin = user?.role === 'SUPER_ADMIN';

    const [formData, setFormData] = useState({
        customerGst: '',
        customerTradeName: '',
        customerName: '',
        customerReferenceName: '',
        customerMobileNumber: '',
        customerEmail: '',
        customerCountry: '',
        customerState: '',
        customerCity: '',
        customerPinCode: '',
        customerStateCode: '',
        customerPanNo: '',
        customerAadhaarNo: '',
        customerTypeId: '',
        saleTypeId: '',
        customerAddress: '',
        company_id: isSuperAdmin ? '' : (user?.company_id || sessionStorage.getItem('company_id') || '')
    });

    const gstRef = useRef(null);

    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [customerTypes, setCustomerTypes] = useState([]);
    const [saleTypes, setSaleTypes] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [allCountries, setAllCountries] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const [loading, setLoading] = useState(false);


    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                gstRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const compId = formData.company_id || sessionStorage.getItem('company_id') || user?.company_id;

            // Fetch Customer Types
            const ctData = await fetchApi(`/customer-type?type=customer${compId ? `&companyId=${compId}` : ''}`);
            setCustomerTypes(Array.isArray(ctData) ? ctData : (ctData.customerTypes || []));

            // Fetch Sale Types
            const stData = await fetchApi(`/sale-type${compId ? `?companyId=${compId}` : ''}`);
            setSaleTypes(Array.isArray(stData) ? stData : (stData.saleTypes || []));

            // Fetch Companies if Super Admin
            if (isSuperAdmin) {
                const cData = await fetchApi('/company');
                setCompanies(cData);
            }

            // Fetch Countries
            const countries = await fetchApi('/country');
            console.log("Fetched countries:", countries);
            setAllCountries(Array.isArray(countries) ? countries : []);
            setCountryList(Array.isArray(countries) ? countries.map(c => c.country_name || c.countryName) : []);
        } catch (error) {
            console.error("Error fetching initial data:", error);
        }
    };

    // Re-fetch types when company changes (for Super Admin)
    useEffect(() => {
        if (isSuperAdmin && formData.company_id) {
            const reFetchTypes = async () => {
                try {
                    const ctData = await fetchApi(`/customer-type?type=customer&company_id=${formData.company_id}`);
                    setCustomerTypes(Array.isArray(ctData) ? ctData : (ctData.customerTypes || []));
                    const stData = await fetchApi(`/sale-type?company_id=${formData.company_id}`);
                    setSaleTypes(Array.isArray(stData) ? stData : (stData.saleTypes || []));
                } catch (error) {
                    console.error("Error re-fetching types:", error);
                }
            };
            reFetchTypes();
        }
    }, [formData.company_id, isSuperAdmin]);

    useEffect(() => {
        const fetchStates = async () => {
            const country = allCountries.find(c => {
                const name = c.country_name || c.countryName;
                return name && formData.customerCountry && name.toLowerCase() === formData.customerCountry.toLowerCase();
            });
            if (country) {
                try {
                    const countryId = country.id || country.countryId;
                    const states = await fetchApi(`/state?countryId=${countryId}`);
                    setAllStates(states);
                    setStateList(states.map(s => s.state_name || s.stateName));
                } catch (error) {
                    console.error("Error fetching states:", error);
                }
            } else {
                setStateList([]);
            }
        };
        fetchStates();
    }, [formData.customerCountry, allCountries]);

    useEffect(() => {
        const fetchCities = async () => {
            const state = allStates.find(s => {
                const name = s.state_name || s.stateName;
                return name && formData.customerState && name.toLowerCase() === formData.customerState.toLowerCase();
            });
            if (state) {
                try {
                    const stateId = state.id || state.stateId;
                    const cities = await fetchApi(`/city?stateId=${stateId}`);
                    setCityList(cities.map(c => c.city_name || c.cityName));
                } catch (error) {
                    console.error("Error fetching cities:", error);
                }
            } else {
                setCityList([]);
            }
        };
        fetchCities();
    }, [formData.customerState, allStates]);

    const handleAddCountry = async (newCountryName) => {
        if (!newCountryName) return;
        try {
            const code = newCountryName.substring(0, 3).toUpperCase();
            const data = await fetchApi('/country', {
                method: 'POST',
                body: JSON.stringify({ countryName: newCountryName, countryCode: code })
            });

            if (data && data.country) {
                setAllCountries(prev => [...prev, data.country]);
                setCountryList(prev => [...prev, data.country.country_name || data.country.countryName]);
                toast.success(`Country "${newCountryName}" added successfully`);
            }
        } catch (error) {
            console.error("Add Country Error:", error);
            toast.error(error.message || "Failed to add country");
        }
    };

    const handleAddState = async (newStateName) => {
        if (!newStateName) return;
        const country = allCountries.find(c => (c.country_name || c.countryName) === formData.customerCountry);
        if (!country) {
            toast.warning("please select country then add new state");
            return;
        }

        try {
            const data = await fetchApi('/state', {
                method: 'POST',
                body: JSON.stringify({ stateName: newStateName, countryId: country.id || country.countryId })
            });
            if (data && data.state) {
                setAllStates(prev => [...prev, data.state]);
                setStateList(prev => [...prev, data.state.state_name || data.state.stateName]);
                toast.success(`State "${newStateName}" added successfully`);
            }
        } catch (error) {
            console.error("Add State Error:", error);
            toast.error(error.message || "Failed to add state");
        }
    };

    const handleAddCity = async (newCityName) => {
        if (!newCityName) return;
        const state = allStates.find(s => (s.state_name || s.stateName) === formData.customerState);
        if (!state) {
            toast.warning("please select state or country then add new city", {
                style: { backgroundColor: '#ffcc00', color: 'black' }
            });
            return;
        }

        try {
            const data = await fetchApi('/city', {
                method: 'POST',
                body: JSON.stringify({ cityName: newCityName, stateId: state.id || state.stateId })
            });

            if (data && data.city) {
                setCityList(prev => [...prev, data.city.city_name || data.city.cityName]);
                toast.success(`City "${newCityName}" added successfully`);
            }
        } catch (error) {
            console.error("Add City Error:", error);
            toast.error(error.message || "Failed to add city");
        }
    };

    const handleAddCustomerType = async (newTypeName) => {
        if (!newTypeName) return;
        try {
            const compId = formData.company_id || sessionStorage.getItem('company_id') || user?.company_id;
            const data = await fetchApi('/customer-type', {
                method: 'POST',
                body: JSON.stringify({ customerTypeName: newTypeName, company_id: compId, type: 'customer' })
            });
            if (data && data.customerType) {
                setCustomerTypes(prev => [...prev, data.customerType]);
                setFormData(prev => ({ ...prev, customerTypeId: data.customerType.id || data.customerType._id }));
                toast.success(`Customer Type "${newTypeName}" added successfully`);
            }
        } catch (error) {
            console.error("Add Customer Type Error:", error);
            toast.error(error.message || "Failed to add customer type");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            const form = e.target.form;
            const index = Array.prototype.indexOf.call(form, e.target);

            // If we are in the address field, skip reset and go to submit
            if (e.target.name === 'customerAddress') {
                e.preventDefault();
                const submitBtn = Array.from(form.elements).find(el => el.type === 'submit' || el.classList.contains('add-customer-btn-submit'));
                if (submitBtn) {
                    submitBtn.focus();
                }
                return;
            }

            if (index > -1 && index < form.elements.length - 1) {
                // If the next element is the verify button, we might want to skip or just focus it
                e.preventDefault();
                form.elements[index + 1]?.focus();
                if (typeof form.elements[index + 1].select === 'function') {
                    form.elements[index + 1].select();
                }
            }
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            // Find inputs in the same column or just go next/prev for simple implementation
            const form = e.target.form;
            const index = Array.prototype.indexOf.call(form, e.target);
            if (e.key === 'ArrowDown' && index < form.elements.length - 1) {
                e.preventDefault();
                form.elements[index + 1]?.focus();
            } else if (e.key === 'ArrowUp' && index > 0) {
                e.preventDefault();
                form.elements[index - 1]?.focus();
            }
        }
    };

    const [isVerifyingGst, setIsVerifyingGst] = useState(false);

    const handleGSTVerify = async (gstToVerify) => {
        const gst = (gstToVerify || '').trim().toUpperCase();
        if (!gst) {
            toast.warn('Please enter a GST number');
            return;
        }
        if (gst.length !== 15) {
            toast.warn('GST number must be exactly 15 characters');
            return;
        }

        setIsVerifyingGst(true);
        try {
            const token = sessionStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/gst/verify/${gst}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await res.json();
            if (res.ok && data.success && data.data) {
                const info = data.data;
                toast.success(`GST verified: ${info.legalName || info.tradeName}`);

                // Extract State Code from GSTIN (first 2 digits)
                const stateCode = gst.substring(0, 2);

                // Auto-determine Customer Type based on Constitution of Business
                let matchedCustomerTypeId = '';
                const companyType = info.businessType || info.taxpayerType || '';
                if (companyType) {
                    const matchedType = customerTypes.find(t => {
                        const name = (t.customerTypeName || t.customer_type_name || t.name || '').toLowerCase();
                        return name === companyType.toLowerCase();
                    });
                    if (matchedType) {
                        matchedCustomerTypeId = matchedType._id || matchedType.id;
                    } else {
                        // If it doesn't exist, auto-add it to save time!
                        try {
                            const compId = formData.company_id || sessionStorage.getItem('company_id') || user?.company_id;
                            const addRes = await fetchApi('/customer-type', {
                                method: 'POST',
                                body: JSON.stringify({ customerTypeName: companyType, company_id: compId, type: 'customer' })
                            });
                            if (addRes && addRes.customerType) {
                                setCustomerTypes(prev => [...prev, addRes.customerType]);
                                matchedCustomerTypeId = addRes.customerType.id || addRes.customerType._id;
                            }
                        } catch (e) { console.error("Auto add customer type error", e); }
                    }
                }

                // Auto-determine Sale Type based on Customer State (24 = Gujarat)
                let matchedSaleTypeId = '';
                if (saleTypes && saleTypes.length > 0) {
                    const isLocal = stateCode === '24';
                    const targetKeyword = isLocal ? '9%' : '18%';

                    const matchedSaleType = saleTypes.find(s => {
                        const name = (s.saleTypeName || s.name || '').toLowerCase();
                        return name.includes(targetKeyword);
                    });

                    if (matchedSaleType) {
                        matchedSaleTypeId = matchedSaleType._id || matchedSaleType.id;
                    } else {
                        matchedSaleTypeId = saleTypes[0]._id || saleTypes[0].id;
                    }
                }

                setFormData(prev => ({
                    ...prev,
                    customerGst: info.gstNumber || prev.customerGst,
                    customerTradeName: info.tradeName || info.legalName || prev.customerTradeName,
                    customerName: info.tradeName || info.legalName || prev.customerName,
                    customerAddress: info.address || prev.customerAddress,
                    customerState: info.state || prev.customerState,
                    customerCity: info.city || prev.customerCity,
                    customerPinCode: info.pincode || prev.customerPinCode,
                    customerPanNo: info.panNumber || prev.customerPanNo,
                    customerStateCode: stateCode || prev.customerStateCode,
                    customerCountry: 'India',
                    customerTypeId: matchedCustomerTypeId || prev.customerTypeId,
                    saleTypeId: matchedSaleTypeId || prev.saleTypeId
                }));
            } else {
                toast.error(data.message || 'GST verification failed');
                // Do not clear fields on failure so the user can type them manually
            }
        } catch (err) {
            console.error('GST verify error:', err);
            toast.error('Error verifying GST details');
            // Do not clear fields on error so the user can type them manually
        } finally {
            setIsVerifyingGst(false);
        }
    };

    const handleChange = (e) => {
                let { name, value } = e.target;
        if (name && name.toLowerCase().includes('gst')) {
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (value.length > 15) value = value.substring(0, 15);
        }

        if (name.toLowerCase().includes('adhar') || name.toLowerCase().includes('aadhar')) {
            let val = value.replace(/\D/g, '');
            if (val.length > 12) val = val.substring(0, 12);
            let formatted = val;
            if (val.length > 4) formatted = val.substring(0, 4) + ' ' + val.substring(4);
            if (val.length > 8) formatted = formatted.substring(0, 9) + ' ' + val.substring(8);
            setFormData(prev => ({ ...prev, [name]: formatted }));
            return;
        }
        if (
            name.toLowerCase().includes('pincode') || 
            name.toLowerCase().includes('pin_code') || 
            name.toLowerCase().includes('accountnumber') || 
            name.toLowerCase().includes('account_number') ||
            name.toLowerCase().includes('accountno') || 
            name.toLowerCase().includes('account_no')
        ) {
            const onlyDigits = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            return;
        }
        
        if (name.toLowerCase().includes('vehicle')) {
            setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
            return;
        }
        if (name.toLowerCase().includes('mobile')) {
            const onlyDigits = value.replace(/\D/g, '');
            if (onlyDigits.length <= 10) {
                setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            }
            return;
        }

        if (name === 'customerGst') {
            const upperVal = value.toUpperCase();
            
            // Auto-select Sale Type based on first 2 digits (State Code)
            let newSaleTypeId = formData.saleTypeId;
            if (upperVal.length >= 2 && saleTypes && saleTypes.length > 0) {
                const stateCode = upperVal.substring(0, 2);
                const isGujarat = stateCode === '24';
                const targetKeyword = isGujarat ? '9%' : '18%';
                
                const matchedSaleType = saleTypes.find(s => {
                    const sName = (s.saleTypeName || s.name || '').toLowerCase();
                    return sName.includes(targetKeyword);
                });
                
                if (matchedSaleType) {
                    newSaleTypeId = matchedSaleType._id || matchedSaleType.id;
                }
            }

            setFormData(prev => ({
                ...prev,
                [name]: upperVal,
                saleTypeId: newSaleTypeId || prev.saleTypeId,
                // Removed aggressive clearing of fields
            }));
            if (upperVal.length === 15) {
                handleGSTVerify(upperVal);
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };


    const handleSubmit = async (e) => {

    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;
        e.preventDefault();
        console.log("Submitting customer form data:", formData);

        if (isSuperAdmin && !formData.company_id) {
            toast.error("Company selection is required");
            return;
        }

        setLoading(true);
        try {
            const submissionData = { ...formData };
            if (!submissionData.customerTypeId || submissionData.customerTypeId === '') delete submissionData.customerTypeId;
            if (!submissionData.saleTypeId || submissionData.saleTypeId === '') delete submissionData.saleTypeId;

            // Important: set company_id if not present for non-admins
            if (!submissionData.company_id && user?.company_id) {
                submissionData.company_id = user.company_id;
            }

            console.log("Sending to API:", submissionData);
            const response = await fetchApi('/customer', {
                method: 'POST',
                body: JSON.stringify(submissionData)
            });
            console.log("API Response:", response);

            if (response && (response.Status !== false)) {
                toast.success('Customer added successfully!');
                if (onSuccess) onSuccess(response.customer || response.Result || response);
                onClose();
            } else {
                toast.error(response.Error || "Failed to add customer");
            }
        } catch (error) {
            console.error("Save Error:", error);
            toast.error(error.message || "Failed to add customer");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFormData({
            customerGst: '',
            customerTradeName: '',
            customerName: '',
            customerReferenceName: '',
            customerMobileNumber: '',
            customerEmail: '',
            customerCountry: '',
            customerState: '',
            customerCity: '',
            customerPinCode: '',
            customerStateCode: '',
            customerPanNo: '',
            customerAadhaarNo: '',
            customerTypeId: '',
            saleTypeId: '',
            customerAddress: '',
            company_id: isSuperAdmin ? '' : (user?.company_id || '')
        });
    };

    if (!isOpen && !isPage) return null;

    const formContent = (
        <div className={isPage ? "page-card" : "add-customer-modal"} onClick={(e) => !isPage && e.stopPropagation()} style={isPage ? { maxWidth: '100%', margin: '0' } : {}}>
            {!isPage && (
                <div className="add-customer-header">
                    <h2 className="add-customer-title">Add Customer</h2>
                    <button className="add-customer-close" onClick={onClose}>✕</button>
                </div>
            )}
            {isPage && <div className="page-card__title">Add Customer</div>}

            <div className={isPage ? "page-card__body" : ""}>
                <form className="add-customer-form" onSubmit={handleSubmit} style={isPage ? { padding: '0' } : {}}>
                    {/* Row 1 - 5 columns */}
                    <div className="add-customer-row">
                        {isSuperAdmin && (
                            <div className="add-customer-field">
                                <label className="add-customer-label">Select Company</label>
                                <select
                                    className="add-customer-select"
                                    name="company_id"
                                    value={formData.company_id}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                >
                                    <option value="">--Select Company--</option>
                                    {companies.map(c => (
                                        <option key={c._id || c.id} value={c._id || c.id}>{c.customerName || c.companyName}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="add-customer-field-with-btn add-customer-field-span-2">
                            <label className="add-customer-label">Customer GST</label>
                            <div className="add-customer-input-group">
                                <input
                                    ref={gstRef}
                                    className="add-customer-input"
                                    placeholder="ENTER CUSTOMER GST"
                                    name="customerGst"
                                    value={formData.customerGst}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                />
                                <button
                                    type="button"
                                    className="add-customer-btn-verify"
                                    onClick={() => handleGSTVerify(formData.customerGst)}
                                    disabled={isVerifyingGst}
                                    onKeyDown={handleKeyDown}
                                >
                                    {isVerifyingGst ? 'Verifying...' : 'Verify'}
                                </button>
                            </div>
                        </div>

                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer Trade Name</label>
                            <input
                                className="add-customer-input"
                                placeholder="Enter Customer Trade Name"
                                name="customerTradeName"
                                value={formData.customerTradeName}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer Name</label>
                            <input
                                className="add-customer-input"
                                placeholder="Enter Customer Name"
                                name="customerName"
                                value={formData.customerName}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer Reference Name</label>
                            <input
                                className="add-customer-input"
                                placeholder="Enter Reference Name"
                                name="customerReferenceName"
                                value={formData.customerReferenceName}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>

                    <div className="add-customer-row">
                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer Mobile Number</label>
                            <input
                                className="add-customer-input"
                                placeholder="Enter Mobile Number"
                                name="customerMobileNumber"
                                value={formData.customerMobileNumber}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer Email</label>
                            <input
                                className="add-customer-input"
                                type="email"
                                placeholder="Enter Email"
                                name="customerEmail"
                                value={formData.customerEmail}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer Country</label>
                            <CreatableSelect
                                value={formData.customerCountry}
                                options={countryList}
                                onChange={(val) => setFormData(prev => ({ ...prev, customerCountry: val }))}
                                onAddOption={(newVal) => {
                                    handleAddCountry(newVal);
                                    setFormData(prev => ({ ...prev, customerCountry: newVal }));
                                }}
                                placeholder="Select or Type Country"
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer State</label>
                            <CreatableSelect
                                value={formData.customerState}
                                options={stateList}
                                onChange={(val) => setFormData(prev => ({ ...prev, customerState: val }))}
                                onAddOption={(newVal) => {
                                    handleAddState(newVal);
                                    setFormData(prev => ({ ...prev, customerState: newVal }));
                                }}
                                placeholder="Select or Type State"
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer City</label>
                            <CreatableSelect
                                value={formData.customerCity}
                                options={cityList}
                                onChange={(val) => setFormData(prev => ({ ...prev, customerCity: val }))}
                                onAddOption={(newVal) => {
                                    handleAddCity(newVal);
                                    setFormData(prev => ({ ...prev, customerCity: newVal }));
                                }}
                                placeholder="Select or Type City"
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>

                    <div className="add-customer-row">
                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer Pin Code</label>
                            <input
                                className="add-customer-input"
                                placeholder="Enter Customer Pin Code"
                                name="customerPinCode"
                                value={formData.customerPinCode}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer State Code</label>
                            <input
                                className="add-customer-input"
                                placeholder="Enter Customer State Code"
                                name="customerStateCode"
                                value={formData.customerStateCode}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer PAN No</label>
                            <input
                                className="add-customer-input"
                                placeholder="Enter Customer PAN No"
                                name="customerPanNo"
                                value={formData.customerPanNo}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer Aadhaar No</label>
                            <input
                                className="add-customer-input"
                                placeholder="Enter Aadhaar Card No"
                                name="customerAadhaarNo"
                                value={formData.customerAadhaarNo}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer Type</label>
                            <CreatableSelect
                                value={customerTypes.find(t => (t._id || t.id) === formData.customerTypeId)?.customerTypeName || ''}
                                options={customerTypes.map(t => t.customerTypeName || t.customer_type_name || t.name)}
                                onChange={(val) => {
                                    const selected = customerTypes.find(t => (t.customerTypeName || t.customer_type_name || t.name) === val);
                                    if (selected) setFormData(prev => ({ ...prev, customerTypeId: selected._id || selected.id }));
                                }}
                                onAddOption={(newVal) => handleAddCustomerType(newVal)}
                                onKeyDown={handleKeyDown}
                                placeholder="Select Type"
                            />
                        </div>
                        <div className="add-customer-field">
                            <label className="add-customer-label">Customer Sale Type</label>
                            <CreatableSelect
                                value={saleTypes.find(s => (s._id || s.id) === formData.saleTypeId)?.saleTypeName || ''}
                                options={saleTypes.map(s => s.saleTypeName || s.name)}
                                onChange={(val) => {
                                    const selected = saleTypes.find(s => (s.saleTypeName || s.name) === val);
                                    if (selected) setFormData(prev => ({ ...prev, saleTypeId: selected._id || selected.id }));
                                }}
                                onAddOption={null}
                                onKeyDown={handleKeyDown}
                                placeholder="Select Sale Type"
                            />
                        </div>
                    </div>


                    {/* Row 4 - Full width textarea */}
                    <div className="add-customer-field add-customer-field-full">
                        <label className="add-customer-label">Customer Address</label>
                        <textarea
                            className="add-customer-textarea"
                            placeholder="Enter Customer Address"
                            name="customerAddress"
                            value={formData.customerAddress}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="add-customer-actions">
                        {!isPage && <button type="button" className="add-customer-btn-reset" onClick={handleReset}>Reset</button>}
                        {isPage && <button type="button" className="add-customer-btn-reset" onClick={onClose} style={{ background: '#fff', border: '1px solid #ddd', color: '#333' }}>Cancel</button>}
                        <button type="submit" className="add-customer-btn-submit">Submit</button>
                    </div>
                </form>
            </div>
        </div>
    );

    if (!isOpen && !isPage) return null;

    if (isPage) return formContent;

    return (
        <div className="add-customer-modal-overlay" onClick={onClose}>
            {formContent}
        </div>
    );
}

export default AddCustomerModal;
